import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Home, ChevronRight, UserPlus, Plus, Trash2, UploadCloud } from 'lucide-react';

const ROLE_OPTIONS = [
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'company_admin', label: 'Company Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'driver', label: 'Driver' },
  { value: 'accountant', label: 'Accountant' },
  { value: 'warehouse_staff', label: 'Warehouse Staff' }
];

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const AddUser = () => {
  const navigate = useNavigate();
  const { id: userId } = useParams();
  const isEditMode = Boolean(userId);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [companyOptions, setCompanyOptions] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [driveConnected, setDriveConnected] = useState(false);
  const [uploadingField, setUploadingField] = useState('');
  const [uploadError, setUploadError] = useState('');

  const authToken = useMemo(() => localStorage.getItem('auth_token'), []);

  const getDownloadUrl = (fileId) => {
    if (!backendUrl || !fileId || !authToken) {
      return null;
    }
    return `${backendUrl}/api/drive/files/${fileId}/download?token=${encodeURIComponent(authToken)}`;
  };

  const [userData, setUserData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    profilePhotoName: '',
    profilePhotoId: '',
    email: '',
    officeEmail: '',
    username: '',
    password: '',
    primaryMobile: '',
    secondaryMobile: '',
    gender: '',
    dateOfBirth: '',
    bloodGroup: '',
    isMarried: 'no',
    anniversaryDate: '',
    companyId: '',
    branchId: '',
    role: '',
    status: 'active',
    dateOfJoining: '',
    aadhaarNumber: '',
    panNumber: '',
    addressLine1: '',
    state: '',
    city: '',
    country: '',
    postalCode: '',
    addressProofName: '',
    addressProofId: '',
    bankName: '',
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
    bankBranch: '',
    passbookChequeName: '',
    passbookChequeId: '',
    pfNumber: '',
    esicNumber: '',
    designation: ''
  });

  const [kycDocuments, setKycDocuments] = useState([
    { id: `kyc-${Date.now()}`, documentName: '', documentFileName: '', documentFileId: '' }
  ]);

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user_profile') || '{}');
    } catch (error) {
      return {};
    }
  }, []);

  const companyProfile = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('company_profile') || '{}');
    } catch (error) {
      return {};
    }
  }, []);

  const isSuperAdmin = currentUser?.role === 'super_admin';

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      throw new Error('Please log in to manage users.');
    }

    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const getErrorMessage = async (response, fallbackMessage) => {
    try {
      const data = await response.json();
      return data?.detail || fallbackMessage;
    } catch (error) {
      return fallbackMessage;
    }
  };

  const handleUserChange = (field, value) => {
    setUserData(prev => ({ ...prev, [field]: value }));
  };

  const fetchDriveStatus = async () => {
    if (!backendUrl) {
      return;
    }
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${backendUrl}/api/drive/status`, { headers });
      if (response.ok) {
        const data = await response.json();
        setDriveConnected(Boolean(data.connected));
      }
    } catch (error) {
      setDriveConnected(false);
    }
  };

  const uploadFileToDrive = async (file, fieldName, idFieldName, kycId = null) => {
    if (!file) {
      return;
    }
    if (!backendUrl) {
      setUploadError('Backend URL is not configured.');
      return;
    }
    if (!driveConnected) {
      setUploadError('Drive is not connected yet. Please contact Super Admin.');
      return;
    }
    try {
      setUploadingField(kycId ? `kyc-${kycId}` : fieldName);
      setUploadError('');
      const headers = getAuthHeaders();
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${backendUrl}/api/drive/upload?module=user_files`, {
        method: 'POST',
        headers: { Authorization: headers.Authorization },
        body: formData
      });
      if (!response.ok) {
        const message = await getErrorMessage(response, 'Failed to upload file.');
        throw new Error(message);
      }
      const data = await response.json();
      if (kycId) {
        setKycDocuments(prev => prev.map(doc => (
          doc.id === kycId
            ? { ...doc, documentFileName: data.file_name, documentFileId: data.file_id }
            : doc
        )));
      } else {
        handleUserChange(fieldName, data.file_name);
        handleUserChange(idFieldName, data.file_id);
      }
    } catch (error) {
      setUploadError(error.message || 'File upload failed.');
    } finally {
      setUploadingField('');
    }
  };

  const handleFileChange = (fieldName, idFieldName, file) => {
    uploadFileToDrive(file, fieldName, idFieldName);
  };

  const handleKycFileUpload = (kycId, file) => {
    uploadFileToDrive(file, '', '', kycId);
  };

  const handleKycChange = (kycId, field, value) => {
    setKycDocuments(prev => prev.map(doc => (doc.id === kycId ? { ...doc, [field]: value } : doc)));
  };

  const addKycDocument = () => {
    setKycDocuments(prev => [...prev, { id: `kyc-${Date.now()}`, documentName: '', documentFileName: '', documentFileId: '' }]);
  };

  const removeKycDocument = (kycId) => {
    if (kycDocuments.length > 1) {
      setKycDocuments(prev => prev.filter(doc => doc.id !== kycId));
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%';
    let generated = '';
    for (let i = 0; i < 10; i += 1) {
      generated += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    handleUserChange('password', generated);
  };

  const validateForm = () => {
    if (!userData.firstName || !userData.lastName || !userData.email || !userData.username || !userData.primaryMobile) {
      setErrorMessage('Please fill in all required personal fields.');
      return false;
    }
    if (!isEditMode && !userData.password) {
      setErrorMessage('Please set a password for the user.');
      return false;
    }
    if (!userData.gender || !userData.dateOfBirth || !userData.bloodGroup) {
      setErrorMessage('Please complete gender, date of birth, and blood group.');
      return false;
    }
    if (!userData.companyId || !userData.branchId || !userData.role) {
      setErrorMessage('Please select company, branch, and role.');
      return false;
    }
    if (!userData.dateOfJoining || !userData.aadhaarNumber || !userData.panNumber) {
      setErrorMessage('Please complete joining date, Aadhaar, and PAN.');
      return false;
    }
    if (!userData.addressLine1 || !userData.state || !userData.city || !userData.country || !userData.postalCode) {
      setErrorMessage('Please complete the permanent address section.');
      return false;
    }

    return true;
  };

  const fetchCompanies = async () => {
    if (!backendUrl) {
      throw new Error('Backend URL is not configured.');
    }
    const headers = getAuthHeaders();
    const response = await fetch(`${backendUrl}/api/companies`, { headers });
    if (!response.ok) {
      const message = await getErrorMessage(response, 'Failed to load companies.');
      throw new Error(message);
    }
    const data = await response.json();
    setCompanyOptions(data);
  };

  const fetchBranches = async (companyId) => {
    if (!backendUrl || !companyId) {
      return;
    }
    const headers = getAuthHeaders();
    const response = await fetch(`${backendUrl}/api/branches?company_id=${companyId}`, { headers });
    if (response.ok) {
      const data = await response.json();
      setBranchOptions(data);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        setErrorMessage('');

        if (isSuperAdmin) {
          await fetchCompanies();
        } else if (companyProfile?.id) {
          setCompanyOptions([companyProfile]);
          setUserData(prev => ({ ...prev, companyId: companyProfile.id }));
          await fetchBranches(companyProfile.id);
        }
        await fetchDriveStatus();
      } catch (error) {
        setErrorMessage(error.message || 'Failed to load companies.');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [isSuperAdmin]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('drive_connected') === 'true') {
      fetchDriveStatus();
      params.delete('drive_connected');
      const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  useEffect(() => {
    if (!userData.companyId) {
      return;
    }

    fetchBranches(userData.companyId);
  }, [userData.companyId]);

  useEffect(() => {
    const loadUser = async () => {
      if (!isEditMode) {
        return;
      }

      try {
        setLoading(true);
        setErrorMessage('');
        const headers = getAuthHeaders();
        const response = await fetch(`${backendUrl}/api/users/${userId}`, { headers });
        if (!response.ok) {
          const message = await getErrorMessage(response, 'Failed to load user details.');
          throw new Error(message);
        }
        const data = await response.json();

        setUserData(prev => ({
          ...prev,
          firstName: data.first_name || '',
          middleName: data.middle_name || '',
          lastName: data.last_name || '',
          profilePhotoName: data.profile_photo_name || '',
          profilePhotoId: data.profile_photo_id || '',
          email: data.email || '',
          officeEmail: data.office_email || '',
          username: data.username || '',
          password: '',
          primaryMobile: data.phone || '',
          secondaryMobile: data.phone_secondary || '',
          gender: data.gender || '',
          dateOfBirth: data.date_of_birth || '',
          bloodGroup: data.blood_group || '',
          isMarried: data.is_married ? 'yes' : 'no',
          anniversaryDate: data.anniversary_date || '',
          companyId: data.company_id || '',
          branchId: data.branch_id || '',
          role: data.role || '',
          status: data.status || 'active',
          dateOfJoining: data.date_of_joining || '',
          aadhaarNumber: data.aadhaar_number || '',
          panNumber: data.pan_number || '',
          addressLine1: data.address_line1 || '',
          state: data.state || '',
          city: data.city || '',
          country: data.country || '',
          postalCode: data.postal_code || '',
          addressProofName: data.address_proof_name || '',
          addressProofId: data.address_proof_id || '',
          bankName: data.bank_name || '',
          accountHolderName: data.account_holder_name || '',
          accountNumber: data.account_number || '',
          ifscCode: data.ifsc_code || '',
          bankBranch: data.bank_branch || '',
          passbookChequeName: data.passbook_cheque_name || '',
          passbookChequeId: data.passbook_cheque_id || '',
          pfNumber: data.pf_number || '',
          esicNumber: data.esic_number || '',
          designation: data.designation || ''
        }));

        if (Array.isArray(data.kyc_documents) && data.kyc_documents.length > 0) {
          setKycDocuments(data.kyc_documents.map((doc, index) => ({
            id: `kyc-${index}-${Date.now()}`,
            documentName: doc.document_name || '',
            documentFileName: doc.document_file_name || '',
            documentFileId: doc.document_file_id || ''
          })));
        }
      } catch (error) {
        setErrorMessage(error.message || 'Failed to load user details.');
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [isEditMode, userId]);

  const handleSave = async () => {
    try {
      setErrorMessage('');
      setSuccessMessage('');

      if (!validateForm()) {
        return;
      }

      setSaving(true);

      const headers = getAuthHeaders();
      const payload = {
        company_id: userData.companyId,
        branch_id: userData.branchId,
        email: userData.email,
        office_email: userData.officeEmail || null,
        username: userData.username,
        first_name: userData.firstName,
        middle_name: userData.middleName || null,
        last_name: userData.lastName,
        full_name: `${userData.firstName} ${userData.middleName ? `${userData.middleName} ` : ''}${userData.lastName}`.trim(),
        role: userData.role,
        phone: userData.primaryMobile,
        phone_secondary: userData.secondaryMobile || null,
        gender: userData.gender,
        date_of_birth: userData.dateOfBirth || null,
        blood_group: userData.bloodGroup,
        is_married: userData.isMarried === 'yes',
        anniversary_date: userData.anniversaryDate || null,
        date_of_joining: userData.dateOfJoining || null,
        aadhaar_number: userData.aadhaarNumber,
        pan_number: userData.panNumber,
        address_line1: userData.addressLine1,
        state: userData.state,
        city: userData.city,
        country: userData.country,
        postal_code: userData.postalCode,
        address_proof_name: userData.addressProofName || null,
        address_proof_id: userData.addressProofId || null,
        profile_photo_name: userData.profilePhotoName || null,
        profile_photo_id: userData.profilePhotoId || null,
        bank_name: userData.bankName,
        account_holder_name: userData.accountHolderName,
        account_number: userData.accountNumber,
        ifsc_code: userData.ifscCode,
        bank_branch: userData.bankBranch,
        passbook_cheque_name: userData.passbookChequeName || null,
        passbook_cheque_id: userData.passbookChequeId || null,
        pf_number: userData.pfNumber || null,
        esic_number: userData.esicNumber || null,
        kyc_documents: kycDocuments
          .filter(doc => doc.documentName)
          .map(doc => ({
            document_name: doc.documentName,
            document_file_name: doc.documentFileName || null,
            document_file_id: doc.documentFileId || null
          })),
        designation: userData.designation || null,
        status: userData.status
      };

      if (!payload.kyc_documents.length) {
        payload.kyc_documents = null;
      }

      if (!isEditMode) {
        payload.password = userData.password;
      } else if (userData.password) {
        payload.password = userData.password;
      }

      const response = await fetch(
        `${backendUrl}/api/users${isEditMode ? `/${userId}` : ''}`,
        {
          method: isEditMode ? 'PUT' : 'POST',
          headers,
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        const message = await getErrorMessage(response, 'Failed to save user.');
        throw new Error(message);
      }

      setSuccessMessage(isEditMode ? 'User updated successfully.' : 'User created successfully.');
      setTimeout(() => navigate('/settings/users'), 800);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to save user.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm font-ibm">
        <Home className="w-4 h-4 text-text-secondary" />
        <span className="text-text-secondary">Home</span>
        <ChevronRight className="w-4 h-4 text-text-secondary" />
        <UserPlus className="w-4 h-4 text-text-secondary" />
        <span className="text-text-secondary">Settings</span>
        <ChevronRight className="w-4 h-4 text-text-secondary" />
        <span data-testid="user-breadcrumb-label" className="text-accent-blue font-medium">
          {isEditMode ? 'Edit Employee' : 'Add Employee'}
        </span>
      </div>

      <div>
        <h1 data-testid="user-form-heading" className="text-3xl font-bold text-text-dark font-manrope">
          {isEditMode ? 'Edit Employee' : 'Add Employee'}
        </h1>
      </div>

      {(errorMessage || successMessage) && (
        <div className="space-y-3">
          {errorMessage && (
            <div
              data-testid="user-form-error"
              className="rounded-lg border border-accent-red/40 bg-accent-red/10 px-4 py-3 text-sm text-accent-red font-ibm"
            >
              {errorMessage}
            </div>
          )}
          {successMessage && (
            <div
              data-testid="user-form-success"
              className="rounded-lg border border-accent-green/40 bg-accent-green/10 px-4 py-3 text-sm text-accent-green font-ibm"
            >
              {successMessage}
            </div>
          )}
        </div>
      )}

      {loading && (
        <div
          data-testid="user-form-loading"
          className="rounded-lg border border-accent-blue/30 bg-accent-blue/10 px-4 py-3 text-sm text-accent-blue font-ibm"
        >
          Loading employee details...
        </div>
      )}

      {uploadError && (
        <div
          data-testid="user-file-upload-error"
          className="rounded-lg border border-accent-red/40 bg-accent-red/10 px-4 py-3 text-sm text-accent-red font-ibm"
        >
          {uploadError}
        </div>
      )}

      {uploadingField && (
        <div
          data-testid="user-file-uploading"
          className="rounded-lg border border-accent-blue/30 bg-accent-blue/10 px-4 py-3 text-sm text-accent-blue font-ibm"
        >
          Uploading file to Google Drive...
        </div>
      )}


      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="border-b-4 border-accent-blue px-6 py-4">
          <h2 data-testid="personal-info-heading" className="text-lg font-semibold text-text-dark font-manrope">
            PERSONAL INFO
          </h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">First Name*</Label>
              <Input
                data-testid="user-first-name-input"
                placeholder="First Name"
                value={userData.firstName}
                onChange={(e) => handleUserChange('firstName', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Middle Name</Label>
              <Input
                data-testid="user-middle-name-input"
                placeholder="Middle Name"
                value={userData.middleName}
                onChange={(e) => handleUserChange('middleName', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Last Name*</Label>
              <Input
                data-testid="user-last-name-input"
                placeholder="Last Name"
                value={userData.lastName}
                onChange={(e) => handleUserChange('lastName', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Picture*</Label>
              <div className="flex items-center gap-3">
                <Input
                  data-testid="user-photo-input"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange('profilePhotoName', 'profilePhotoId', e.target.files?.[0])}
                  disabled={!driveConnected || uploadingField === 'profilePhotoName'}
                  className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm disabled:bg-gray-50 disabled:cursor-not-allowed"
                />
                <UploadCloud className="w-5 h-5 text-text-secondary" />
              </div>
              {userData.profilePhotoName && (
                <div className="flex items-center gap-3">
                  <p data-testid="user-photo-name" className="text-xs text-text-secondary font-ibm">
                    {userData.profilePhotoName}
                  </p>
                  {userData.profilePhotoId && (
                    <a
                      data-testid="user-photo-download"
                      href={getDownloadUrl(userData.profilePhotoId)}
                      className="text-xs text-accent-blue font-ibm"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Download
                    </a>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Email Id*</Label>
              <Input
                data-testid="user-email-input"
                type="email"
                placeholder="Email Id"
                value={userData.email}
                onChange={(e) => handleUserChange('email', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Office Email Id</Label>
              <Input
                data-testid="user-office-email-input"
                type="email"
                placeholder="Office Email Id"
                value={userData.officeEmail}
                onChange={(e) => handleUserChange('officeEmail', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Username*</Label>
              <Input
                data-testid="user-username-input"
                placeholder="Username"
                value={userData.username}
                onChange={(e) => handleUserChange('username', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                required
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-text-dark font-ibm font-medium">Password*</Label>
                <button
                  type="button"
                  data-testid="generate-password-button"
                  onClick={generatePassword}
                  className="text-xs text-accent-blue font-ibm"
                >
                  Generate
                </button>
              </div>
              <Input
                data-testid="user-password-input"
                type="text"
                placeholder="Password"
                value={userData.password}
                onChange={(e) => handleUserChange('password', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                required={!isEditMode}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Primary Mobile*</Label>
              <Input
                data-testid="user-primary-mobile-input"
                placeholder="Primary Mobile"
                value={userData.primaryMobile}
                onChange={(e) => handleUserChange('primaryMobile', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Secondary Mobile</Label>
              <Input
                data-testid="user-secondary-mobile-input"
                placeholder="Secondary Mobile"
                value={userData.secondaryMobile}
                onChange={(e) => handleUserChange('secondaryMobile', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Gender*</Label>
              <Select value={userData.gender} onValueChange={(val) => handleUserChange('gender', val)}>
                <SelectTrigger data-testid="user-gender-select" className="border-gray-200 focus:border-accent-blue font-ibm">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem data-testid="user-gender-option-male" value="male">Male</SelectItem>
                  <SelectItem data-testid="user-gender-option-female" value="female">Female</SelectItem>
                  <SelectItem data-testid="user-gender-option-other" value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Date Of Birth*</Label>
              <Input
                data-testid="user-dob-input"
                type="date"
                value={userData.dateOfBirth}
                onChange={(e) => handleUserChange('dateOfBirth', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Blood Group*</Label>
              <Select value={userData.bloodGroup} onValueChange={(val) => handleUserChange('bloodGroup', val)}>
                <SelectTrigger data-testid="user-blood-group-select" className="border-gray-200 focus:border-accent-blue font-ibm">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {BLOOD_GROUPS.map(group => (
                    <SelectItem key={group} data-testid={`user-blood-group-${group}`} value={group}>
                      {group}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Married*</Label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-text-secondary font-ibm">
                  <input
                    type="radio"
                    data-testid="user-married-yes"
                    checked={userData.isMarried === 'yes'}
                    onChange={() => handleUserChange('isMarried', 'yes')}
                  />
                  Yes
                </label>
                <label className="flex items-center gap-2 text-sm text-text-secondary font-ibm">
                  <input
                    type="radio"
                    data-testid="user-married-no"
                    checked={userData.isMarried === 'no'}
                    onChange={() => handleUserChange('isMarried', 'no')}
                  />
                  No
                </label>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Date of Anniversary</Label>
              <Input
                data-testid="user-anniversary-input"
                type="date"
                value={userData.anniversaryDate}
                onChange={(e) => handleUserChange('anniversaryDate', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Company*</Label>
              <Select
                value={userData.companyId}
                onValueChange={(val) => handleUserChange('companyId', val)}
                disabled={!isSuperAdmin}
              >
                <SelectTrigger data-testid="user-company-select" className="border-gray-200 focus:border-accent-blue font-ibm">
                  <SelectValue placeholder="Select Company" />
                </SelectTrigger>
                <SelectContent>
                  {companyOptions.map(company => (
                    <SelectItem
                      key={company.id}
                      data-testid={`user-company-${company.id}`}
                      value={company.id}
                    >
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Branch*</Label>
              <Select value={userData.branchId} onValueChange={(val) => handleUserChange('branchId', val)}>
                <SelectTrigger data-testid="user-branch-select" className="border-gray-200 focus:border-accent-blue font-ibm">
                  <SelectValue placeholder="Select Branch" />
                </SelectTrigger>
                <SelectContent>
                  {branchOptions.map(branch => (
                    <SelectItem
                      key={branch.id}
                      data-testid={`user-branch-${branch.id}`}
                      value={branch.id}
                    >
                      {branch.nick_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Role*</Label>
              <Select value={userData.role} onValueChange={(val) => handleUserChange('role', val)}>
                <SelectTrigger data-testid="user-role-select" className="border-gray-200 focus:border-accent-blue font-ibm">
                  <SelectValue placeholder="Select Role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map(role => (
                    <SelectItem key={role.value} data-testid={`user-role-${role.value}`} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Status</Label>
              <Select value={userData.status} onValueChange={(val) => handleUserChange('status', val)}>
                <SelectTrigger data-testid="user-status-select" className="border-gray-200 focus:border-accent-blue font-ibm">
                  <SelectValue placeholder="Select Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem data-testid="user-status-active" value="active">Active</SelectItem>
                  <SelectItem data-testid="user-status-inactive" value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </Card>

      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="border-b-4 border-accent-green px-6 py-4">
          <h2 data-testid="basic-info-heading" className="text-lg font-semibold text-text-dark font-manrope">
            BASIC INFO
          </h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Date Of Joining*</Label>
              <Input
                data-testid="user-joining-date-input"
                type="date"
                value={userData.dateOfJoining}
                onChange={(e) => handleUserChange('dateOfJoining', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Aadhaar No*</Label>
              <Input
                data-testid="user-aadhaar-input"
                placeholder="Aadhaar No"
                value={userData.aadhaarNumber}
                onChange={(e) => handleUserChange('aadhaarNumber', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">PAN No*</Label>
              <Input
                data-testid="user-pan-input"
                placeholder="PAN No"
                value={userData.panNumber}
                onChange={(e) => handleUserChange('panNumber', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                required
              />
            </div>
          </div>
        </div>
      </Card>

      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="border-b-4 border-accent-orange px-6 py-4">
          <h2 data-testid="address-info-heading" className="text-lg font-semibold text-text-dark font-manrope">
            PERMANENT ADDRESS
          </h2>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="space-y-2 md:col-span-2">
              <Label className="text-text-dark font-ibm font-medium">Address*</Label>
              <Textarea
                data-testid="user-address-input"
                placeholder="Address"
                value={userData.addressLine1}
                onChange={(e) => handleUserChange('addressLine1', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm min-h-[80px]"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">State*</Label>
              <Input
                data-testid="user-state-input"
                placeholder="State"
                value={userData.state}
                onChange={(e) => handleUserChange('state', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Town/City*</Label>
              <Input
                data-testid="user-city-input"
                placeholder="Town/City"
                value={userData.city}
                onChange={(e) => handleUserChange('city', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Country*</Label>
              <Input
                data-testid="user-country-input"
                placeholder="Country"
                value={userData.country}
                onChange={(e) => handleUserChange('country', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Postal Code*</Label>
              <Input
                data-testid="user-postal-code-input"
                placeholder="Postal Code"
                value={userData.postalCode}
                onChange={(e) => handleUserChange('postalCode', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                required
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-text-dark font-ibm font-medium">Address Proof</Label>
              <Input
                data-testid="user-address-proof-input"
                type="file"
                onChange={(e) => handleFileChange('addressProofName', 'addressProofId', e.target.files?.[0])}
                disabled={!driveConnected || uploadingField === 'addressProofName'}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm disabled:bg-gray-50 disabled:cursor-not-allowed"
              />
              {userData.addressProofName && (
                <div className="flex items-center gap-3">
                  <p data-testid="user-address-proof-name" className="text-xs text-text-secondary font-ibm">
                    {userData.addressProofName}
                  </p>
                  {userData.addressProofId && (
                    <a
                      data-testid="user-address-proof-download"
                      href={getDownloadUrl(userData.addressProofId)}
                      className="text-xs text-accent-blue font-ibm"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Download
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="border-b-4 border-accent-cyan px-6 py-4">
          <h2 data-testid="bank-info-heading" className="text-lg font-semibold text-text-dark font-manrope">
            BANK DETAILS
          </h2>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Bank Name</Label>
              <Input
                data-testid="user-bank-name-input"
                placeholder="Bank Name"
                value={userData.bankName}
                onChange={(e) => handleUserChange('bankName', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Account Holder Name</Label>
              <Input
                data-testid="user-account-holder-input"
                placeholder="Account Holder Name"
                value={userData.accountHolderName}
                onChange={(e) => handleUserChange('accountHolderName', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Account No</Label>
              <Input
                data-testid="user-account-number-input"
                placeholder="Account No"
                value={userData.accountNumber}
                onChange={(e) => handleUserChange('accountNumber', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">IFSC Code</Label>
              <Input
                data-testid="user-ifsc-input"
                placeholder="IFSC Code"
                value={userData.ifscCode}
                onChange={(e) => handleUserChange('ifscCode', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">Branch</Label>
              <Input
                data-testid="user-bank-branch-input"
                placeholder="Branch"
                value={userData.bankBranch}
                onChange={(e) => handleUserChange('bankBranch', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-text-dark font-ibm font-medium">Passbook/Blank Cheque</Label>
              <Input
                data-testid="user-passbook-input"
                type="file"
                onChange={(e) => handleFileChange('passbookChequeName', 'passbookChequeId', e.target.files?.[0])}
                disabled={!driveConnected || uploadingField === 'passbookChequeName'}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm disabled:bg-gray-50 disabled:cursor-not-allowed"
              />
              {userData.passbookChequeName && (
                <div className="flex items-center gap-3">
                  <p data-testid="user-passbook-name" className="text-xs text-text-secondary font-ibm">
                    {userData.passbookChequeName}
                  </p>
                  {userData.passbookChequeId && (
                    <a
                      data-testid="user-passbook-download"
                      href={getDownloadUrl(userData.passbookChequeId)}
                      className="text-xs text-accent-blue font-ibm"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Download
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="border-b-4 border-accent-purple px-6 py-4">
          <h2 data-testid="required-documents-heading" className="text-lg font-semibold text-text-dark font-manrope">
            REQUIRED DOCUMENTS
          </h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">PF No.</Label>
              <Input
                data-testid="user-pf-input"
                placeholder="Enter PF No."
                value={userData.pfNumber}
                onChange={(e) => handleUserChange('pfNumber', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-text-dark font-ibm font-medium">ESIC No.</Label>
              <Input
                data-testid="user-esic-input"
                placeholder="ESIC No."
                value={userData.esicNumber}
                onChange={(e) => handleUserChange('esicNumber', e.target.value)}
                className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
              />
            </div>
          </div>
        </div>
      </Card>

      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="border-b-4 border-accent-blue px-6 py-4 flex items-center justify-between">
          <h2 data-testid="kyc-documents-heading" className="text-lg font-semibold text-text-dark font-manrope">
            KYC DOCUMENTS
          </h2>
          <Button
            data-testid="add-kyc-document-button"
            variant="outline"
            onClick={addKycDocument}
            className="border-accent-blue text-accent-blue hover:bg-accent-blue hover:text-white font-ibm font-semibold h-9 px-4 rounded-lg"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New
          </Button>
        </div>
        <div className="p-6 space-y-6">
          {kycDocuments.map((doc, index) => (
            <div key={doc.id} className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">Document Name</Label>
                <Input
                  data-testid={`kyc-document-name-${doc.id}`}
                  placeholder="Document Name"
                  value={doc.documentName}
                  onChange={(e) => handleKycChange(doc.id, 'documentName', e.target.value)}
                  className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-text-dark font-ibm font-medium">Document</Label>
                <Input
                  data-testid={`kyc-document-file-${doc.id}`}
                  type="file"
                  onChange={(e) => handleKycFileUpload(doc.id, e.target.files?.[0])}
                  disabled={!driveConnected || uploadingField === `kyc-${doc.id}`}
                  className="border-gray-200 focus:border-accent-blue focus:ring-accent-blue font-ibm disabled:bg-gray-50 disabled:cursor-not-allowed"
                />
                {doc.documentFileName && (
                  <div className="flex items-center gap-3">
                    <p data-testid={`kyc-document-file-name-${doc.id}`} className="text-xs text-text-secondary font-ibm">
                      {doc.documentFileName}
                    </p>
                    {doc.documentFileId && (
                      <a
                        data-testid={`kyc-document-download-${doc.id}`}
                        href={getDownloadUrl(doc.documentFileId)}
                        className="text-xs text-accent-blue font-ibm"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Download
                      </a>
                    )}
                  </div>
                )}
              </div>
              {index > 0 && (
                <div className="md:col-span-2">
                  <Button
                    data-testid={`remove-kyc-document-${doc.id}`}
                    variant="outline"
                    onClick={() => removeKycDocument(doc.id)}
                    className="border-accent-red text-accent-red hover:bg-accent-red hover:text-white font-ibm font-semibold h-9 px-4 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      <div className="flex items-center justify-end gap-4 pb-8">
        <Button
          data-testid="cancel-user-button"
          variant="outline"
          onClick={() => navigate('/settings/users')}
          disabled={saving}
          className="bg-accent-orange hover:bg-accent-orange/90 text-white border-0 font-ibm font-semibold h-11 px-8 rounded-lg disabled:opacity-70"
        >
          Cancel
        </Button>
        <Button
          data-testid="save-user-button"
          onClick={handleSave}
          disabled={saving || loading}
          className="bg-accent-green hover:bg-accent-green/90 text-white font-ibm font-semibold h-11 px-8 rounded-lg disabled:opacity-70"
        >
          {saving ? 'Saving...' : isEditMode ? 'Update Employee' : 'Save Employee'}
        </Button>
      </div>
    </div>
  );
};

export default AddUser;
